import { Permission } from 'v2/models';

type tPermNames =
  | 'CREATE_SEARCH'
  | 'CREATE_INTERNAL_SEARCH'
  | 'VIEW_SEARCHES_ORG'
  | 'VIEW_SEARCHES_MASTER_ORG'
  | 'MONITOR_SEARCHES_ORG'
  | 'MONITOR_SEARCHES_MASTER_ORG'
  | 'REVIEW_SEARCH_ORG'
  | 'REVIEW_SEARCH_MASTER_ORG'
  | 'DRM'
  | 'CREATE_SEARCH_OWN'
  | 'CREATE_INTERNAL_SEARCH_OWN'
  | 'DOCUMENT_CHAT'
  | 'DRM_CHAT'
  | 'EXTENDED_REQUESTS'
  | 'FEATURED_PROVIDERS'
  | 'CREATE_SEARCH_FREE';

type PermissionName = Record<tPermNames, string>;

export const PERMISSION_NAME: PermissionName = {
  CREATE_SEARCH: 'create-search',
  CREATE_INTERNAL_SEARCH: 'create-internal-search',
  VIEW_SEARCHES_ORG: 'view-searches-org',
  VIEW_SEARCHES_MASTER_ORG: 'view-searches-master-org',
  MONITOR_SEARCHES_ORG: 'monitor-searches-org',
  MONITOR_SEARCHES_MASTER_ORG: 'monitor-searches-master-org',
  REVIEW_SEARCH_ORG: 'review-search-org',
  REVIEW_SEARCH_MASTER_ORG: 'review-search-master-org',
  DRM: 'drm',
  CREATE_SEARCH_OWN: 'create-search-own',
  CREATE_INTERNAL_SEARCH_OWN: 'create-internal-search-own',
  DOCUMENT_CHAT: 'document_chat',
  DRM_CHAT: 'drm_chat',
  EXTENDED_REQUESTS: 'extended_requests',
  FEATURED_PROVIDERS: 'featured_providers',
  CREATE_SEARCH_FREE: 'create-search-free',
};

export const REVIEWER_PERMISSIONS = [
  PERMISSION_NAME.REVIEW_SEARCH_ORG,
  PERMISSION_NAME.REVIEW_SEARCH_MASTER_ORG,
];

export const EXTERNAL_SEARCHER_PERMISSIONS = [
  PERMISSION_NAME.CREATE_SEARCH,
  PERMISSION_NAME.CREATE_SEARCH_OWN,
  PERMISSION_NAME.CREATE_SEARCH_FREE,
];

export const SEARCHER_PERMISSIONS = [
  PERMISSION_NAME.CREATE_SEARCH,
  PERMISSION_NAME.CREATE_SEARCH_OWN,
  PERMISSION_NAME.CREATE_INTERNAL_SEARCH,
  PERMISSION_NAME.CREATE_INTERNAL_SEARCH_OWN,
];

export const INTERNAL_SEARCHER_PERMISSIONS = [
  PERMISSION_NAME.CREATE_INTERNAL_SEARCH,
  PERMISSION_NAME.CREATE_INTERNAL_SEARCH_OWN,
];

export const PLAN_ID = {
  SEARCHER_BASIC: 'searcher-basic',
};

export const PERMISSIONS_PAGE_SIZE = 20;

const createPermissionsList = (permissionsState: {
  [key: string]: Permission;
}): Permission[] => {
  return Object.values(permissionsState);
};

/**
 *
 * @param permissionsState
 * @param permissionList
 * @param validateAll
 * @returns Boolean indicating if user has one or all permissions in permissionList
 */
export function hasPermission(
  permissionsState: { [key: string]: Permission },
  permissionList: string[],
  validateAll?: boolean
) {
  const permissions = createPermissionsList(permissionsState);

  const permissionsMatch: Permission[] = [];
  for (let index = 0; index < permissions.length; index++) {
    const permission: Permission = permissions[index];
    if (
      permission.subscription_active &&
      permissionList.includes(permission.permission_name)
    ) {
      permissionsMatch.push(permission);
    }
  }

  return validateAll
    ? permissionsMatch.length === permissionList.length
    : permissionsMatch.length > 0;
}

/**
 *
 * @param permissionsState
 * @param permissionList
 * @returns Array with data of the available permissions in permissionList
 */
export const getPermissionData = (
  permissionsState: { [key: string]: Permission },
  permissionList: string[]
) => {
  const permissions = createPermissionsList(permissionsState);

  return permissions.filter((permission: Permission) =>
    permissionList.includes(permission.permission_name)
  );
};

/**
 *
 * @param permissionsState
 * @param planId
 * @returns Boolean indicating if user has the given planId in any of his permissions
 */
export const hasPlanId = (
  permissionsState: { [key: string]: Permission },
  planId: string
) => {
  const permissions = createPermissionsList(permissionsState);

  for (let index = 0; index < permissions.length; index++) {
    if (
      permissions[index].plan_id === planId &&
      permissions[index].subscription_active
    ) {
      return true;
    }
  }
  return false;
};

/**
 *
 * @param permissionsState
 * @returns Boolean indicating if user has any permissions with subscription_active = true
 */
export const hasActivePermissions = (permissionsState: {
  [key: string]: Permission;
}) => {
  const permissions = createPermissionsList(permissionsState);
  return permissions.some((permission) => permission.subscription_active);
};

export const hasPlanFamily = (
  permissionsState: {
    [key: string]: Permission;
  },
  planFamily: 'enterprise' | 'discover'
) => {
  return createPermissionsList(permissionsState).some((permission) => {
    return (
      permission?.plan_family === planFamily && permission?.subscription_active
    );
  });
};

/**
 *
 * @param permissionsState
 * @returns Integer indicating the amount of active permissions
 */
export const totalActivePermisssions = (
  permissionsState: Record<string, Permission>
) => {
  return Object.values(permissionsState).filter(
    (innerObj) => innerObj.subscription_active === true
  ).length;
};

export const isFreeSearcher = (
  permissionsState: Record<string, Permission>
) => {
  const hasFreeSearcherPermission = hasPermission(permissionsState, [
    PERMISSION_NAME.CREATE_SEARCH_FREE,
  ]);
  const hasPaidSearcherPermission = hasPermission(
    permissionsState,
    SEARCHER_PERMISSIONS
  );
  return hasFreeSearcherPermission && !hasPaidSearcherPermission;
};
